/**
 * Share-view bin restore: "pick a folder to restore into".
 *
 * The share-view twin of the Drive bin's restore picker (BinView's
 * `renderRestoreTargetPicker` → FolderPickerDialog). The server refuses a restore
 * whose target is gone / outside the share / hidden / read-only with 409
 * `RESTORE_TARGET_UNAVAILABLE` rather than silently relocating it to the share
 * root; this dialog lets the visitor choose where it goes instead.
 *
 * The Drive picker can't be reused here — it lists through `/api/files`, which a
 * share-link visitor can't call — so this browses with the share view's OWN
 * listing (`listFolder` = ShareFileExplorer's `loadFolder`, the scope-gated
 * `/session/:sid/list`). Any folder can be OPENED (a writable folder may sit
 * under a read-only one), but only folders whose listing `effectivePermission`
 * grants write can be CHOSEN. The server re-judges the choice; this only keeps
 * the visitor from picking something that is certain to be refused.
 *
 * Not filtered here: the #489 app-reach safeguard (a writable folder inside a
 * `.pdapp` whose reach the link lacks). The listing carries no per-row reach
 * verdict, and computing one per listed folder is a principal resolution per row;
 * the server answers such a pick with the same 409 and the picker re-opens, so
 * the visitor just picks another folder.
 */
import { useCallback, useEffect, useState } from 'react'
import { ChevronRight, Loader2 } from 'lucide-react'
import { Modal, FileIcon } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { isPointerRow } from '@papan/types'

export interface PickerFolder {
  id: string
  name: string
  isFolder: boolean
  fileType?: string | null
  effectivePermission?: string
}

export function canWriteLevel(p: string | undefined): boolean {
  return p === 'readwrite' || p === 'admin' || p === 'owner'
}

interface Props {
  originalName: string
  rootFileId: string
  /** The share root's own effective permission (its listing's `parentPermission`). */
  rootPermission: string | undefined
  /** The share view's scope-gated listing of one folder. */
  listFolder: (folderId: string) => Promise<PickerFolder[]>
  onSelect: (folderId: string) => void
  onCancel: () => void
}

export function ShareRestoreTargetPicker({ originalName, rootFileId, rootPermission, listFolder, onSelect, onCancel }: Props) {
  const { t } = useTranslation('files')
  // The drill-down stack: [root, …opened folders]. Each crumb carries its own
  // permission (the root's from the listing header, a child's from its row).
  const [stack, setStack] = useState<Array<{ id: string; name: string; perm: string | undefined }>>(
    () => [{ id: rootFileId, name: t('binView.sharePicker.root'), perm: rootPermission }],
  )
  const current = stack[stack.length - 1]!
  const [rows, setRows] = useState<PickerFolder[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listFolder(current.id)
      .then(items => {
        if (cancelled) return
        // Folders only; a `.shared` / `.chain` mount can't take a restore through
        // this share (the server refuses it as a destination).
        setRows(items.filter(f => f.isFolder && !isPointerRow(f)))
      })
      .catch(() => { if (!cancelled) setRows([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [current.id, listFolder])

  // Default selection: the folder being shown, when the visitor can write it.
  useEffect(() => {
    setSelectedId(canWriteLevel(current.perm) ? current.id : null)
  }, [current.id, current.perm])

  const open = useCallback((f: PickerFolder) => {
    setStack(prev => [...prev, { id: f.id, name: f.name, perm: f.effectivePermission }])
  }, [])

  return (
    <Modal
      title={t('binView.picker.title', { name: originalName })}
      description={t('binView.sharePicker.hint')}
      onClose={onCancel}
      footer={<>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded text-sm themed-text-secondary hover:themed-bg-tertiary">
          {t('common:cancel')}
        </button>
        <button
          type="button"
          data-testid="share-restore-confirm"
          disabled={!selectedId}
          onClick={() => { if (selectedId) onSelect(selectedId) }}
          className="px-3 py-1.5 rounded text-sm themed-btn-primary disabled:opacity-50"
        >
          {t('binView.actions.restoreHere')}
        </button>
      </>}
    >
      <div className="px-3 py-2 flex items-center gap-1 text-xs themed-text-secondary flex-wrap border-b themed-border" data-testid="share-restore-crumbs">
        {stack.map((c, i) => (
          <span key={c.id} className="inline-flex items-center gap-1">
            {i > 0 && <ChevronRight size={11} className="themed-text-muted" />}
            <button
              type="button"
              disabled={i === stack.length - 1}
              onClick={() => setStack(prev => prev.slice(0, i + 1))}
              className="hover:underline disabled:no-underline disabled:themed-text"
            >
              {c.name}
            </button>
          </span>
        ))}
      </div>
      <div className="p-2 min-h-[160px] max-h-[50vh] overflow-auto text-sm" role="listbox" aria-label={t('binView.picker.title', { name: originalName })}>
        {loading ? (
          <div className="flex items-center justify-center p-4"><Loader2 size={16} className="animate-spin themed-text-muted" /></div>
        ) : rows.length === 0 ? (
          <div className="px-2 py-6 text-center text-xs themed-text-muted">{t('binView.sharePicker.noSubfolders')}</div>
        ) : (
          rows.map(f => {
            const writable = canWriteLevel(f.effectivePermission)
            const selected = selectedId === f.id
            return (
              <div key={f.id} className={`flex items-center gap-1 rounded ${selected ? 'themed-bg-tertiary' : ''}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-disabled={!writable}
                  disabled={!writable}
                  data-testid={`share-restore-folder-${f.id}`}
                  onClick={() => setSelectedId(f.id)}
                  onDoubleClick={() => open(f)}
                  className="flex-1 min-w-0 flex items-center gap-2 px-2 py-1 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileIcon name={f.name} isFolder size={14} miniappIconName={f.name} />
                  <span className="truncate themed-text">{f.name}</span>
                  {!writable && <span className="ml-auto text-[10px] themed-text-muted shrink-0">{t('binView.sharePicker.readOnly')}</span>}
                </button>
                <button
                  type="button"
                  onClick={() => open(f)}
                  aria-label={t('binView.sharePicker.openFolder', { name: f.name })}
                  data-testid={`share-restore-open-${f.id}`}
                  className="p-1 rounded themed-text-muted hover:themed-text hover:themed-bg-secondary"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )
          })
        )}
      </div>
    </Modal>
  )
}
